/**
 * Lazy-loaded detail panel for a selected program row on the admin Programs page.
 *
 * The list itself stays light — it renders only what the table columns show. When an
 * admin expands a program we fetch one aggregate (`program_detail_summary`) that carries
 * the enrollment counts, the gross-booking figures, and the program's pricing make-up.
 * Mounted only while the row is expanded, so nothing is fetched for the other ~50 rows.
 *
 * Layout is a single responsive grid: stat tiles wrap 2-up on a phone and 4-up on
 * desktop, and the two detail cards stack below on narrow screens.
 */
import { useQuery } from '@tanstack/react-query';
import { sessionBreakdown, describeBreakdown } from '../../utils/sessionBreakdown';
import { isBrandKey } from '../../config/brands';
import { supabase } from '../supabase';
import { formatDateDisplay } from '../../utils/date';
import { getScheduleDescription } from '../../types/program';
import { collectedPct, money } from '../utils/money';

export interface ProgramDetailProgram {
  id: string;
  name: string;
  description?: string;
  waiver?: string;
  type: string;
  price: number;
  billing_type: 'recurring_monthly' | 'fixed_term' | 'per_day' | 'flex_monthly';
  active: boolean;
  start_date: string;
  end_date: string;
  schedule_cron: string;
  duration: number;
  levels?: string[] | null;
  selective_dates?: string[] | null;
  excluded_dates?: string[] | null;
  /**
   * Which brand's closure calendar this program keeps. Optional because rows selected before
   * the column existed omit it; absent reads as the default brand, matching the column's
   * own DEFAULT.
   */
  brand_key?: string | null;
  invoice_due_date?: string | null;
  created_at?: string;
}

export interface ProgramPricingRow {
  line_item_config_id: string;
  name: string | null;
  type: string | null;
  subtype: string | null;
  amount: number;
  is_optional: boolean;
  is_one_off: boolean;
}

export interface ProgramDetailSummary {
  program_id: string;
  enrollments: { total: number; active: number; inactive: number; students: number };
  booking: {
    invoice_count: number;
    gross_booked: number;
    collected: number;
    outstanding: number;
    overdue: number;
    voided_count: number;
    voided_amount: number;
    refunded: number;
    first_period: string | null;
    last_period: string | null;
    by_status: Array<{ status: string; count: number; amount: number }>;
  };
  pricing: ProgramPricingRow[];
}

const BILLING_LABEL: Record<ProgramDetailProgram['billing_type'], string> = {
  recurring_monthly: 'Monthly',
  fixed_term: 'Fixed term',
  per_day: 'Per day',
  flex_monthly: 'Flex monthly',
};

function Stat({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}) {
  const toneClass =
    tone === 'good' ? 'text-green-700' :
    tone === 'warn' ? 'text-amber-700' :
    tone === 'bad' ? 'text-red-700' :
    'text-gray-900';
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-base font-semibold leading-tight ${toneClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{sub}</div>}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-24 shrink-0 font-medium text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 break-words text-gray-800">{children}</span>
    </div>
  );
}

export function ProgramDetailPanel({ program }: { program: ProgramDetailProgram }) {
  const { data, isLoading, error } = useQuery<ProgramDetailSummary>({
    queryKey: ['program-detail-summary', program.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('program_detail_summary', {
        p_program_id: program.id,
      });
      if (error) throw error;
      return data as unknown as ProgramDetailSummary;
    },
  });

  const booking = data?.booking;
  const pct = booking ? collectedPct(booking.collected, booking.gross_booked) : null;

  return (
    <div className="space-y-3">
      {/* Gross booking */}
      <div>
        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
          Gross booking
        </div>
        {isLoading && <div className="text-xs text-gray-500">Loading program summary…</div>}
        {error && <div className="text-xs text-red-600">Failed to load program summary</div>}
        {booking && (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <Stat
                label="Booked"
                value={money(booking.gross_booked)}
                sub={`${booking.invoice_count} invoice${booking.invoice_count === 1 ? '' : 's'}`}
              />
              <Stat
                label="Collected"
                value={money(booking.collected)}
                sub={pct === null ? undefined : `${pct}% of booked`}
                tone="good"
              />
              <Stat
                label="Outstanding"
                value={money(booking.outstanding)}
                sub={booking.overdue > 0 ? `${money(booking.overdue)} overdue` : undefined}
                tone={booking.outstanding > 0 ? 'warn' : 'default'}
              />
              <Stat
                label="Enrolled"
                value={`${data.enrollments.active}`}
                sub={`${data.enrollments.total} all-time · ${data.enrollments.students} students`}
              />
              <Stat
                label="Avg / enrollment"
                value={
                  data.enrollments.total > 0
                    ? money(booking.gross_booked / data.enrollments.total)
                    : '—'
                }
                sub={
                  booking.first_period && booking.last_period
                    ? `${formatDateDisplay(booking.first_period.slice(0, 10))} – ${formatDateDisplay(booking.last_period.slice(0, 10))}`
                    : 'Not invoiced yet'
                }
              />
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
              <span>Invoiced to date — not a projection of the remaining term.</span>
              {booking.refunded > 0 && <span>Refunded {money(booking.refunded)}</span>}
              {booking.voided_count > 0 && (
                <span>
                  {booking.voided_count} voided ({money(booking.voided_amount)}) excluded
                </span>
              )}
            </div>
            {booking.by_status.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {booking.by_status.map((s) => (
                  <span
                    key={s.status}
                    className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700"
                  >
                    <span className="font-medium capitalize">{s.status}</span>
                    <span className="ml-1 text-gray-500">
                      {s.count} · {money(s.amount)}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Program facts + cost make-up */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
            Program
          </div>
          <div className="space-y-1">
            <Fact label="Billing">
              {BILLING_LABEL[program.billing_type]} · list price {money(program.price)}
            </Fact>
            <Fact label="Runs">
              {formatDateDisplay(program.start_date)} – {formatDateDisplay(program.end_date)}
            </Fact>
            <Fact label="Schedule">
              {getScheduleDescription(program.schedule_cron)}
              {program.duration ? ` · ${program.duration} min` : ''}
            </Fact>
            {program.invoice_due_date && (
              <Fact label="Invoice due">{formatDateDisplay(program.invoice_due_date)}</Fact>
            )}
            {program.levels && program.levels.length > 0 && (
              <Fact label="Levels">{program.levels.join(', ')}</Fact>
            )}
            {program.selective_dates && program.selective_dates.length > 0 && (
              <Fact label="Offered">
                {program.selective_dates.length} selectable date
                {program.selective_dates.length === 1 ? '' : 's'}
              </Fact>
            )}
            <SessionsFact program={program} />
            <Fact label="Waiver">
              {program.waiver && program.waiver.trim() ? 'Set' : 'None'}
            </Fact>
            {program.description && program.description.trim() && (
              <Fact label="About">
                <span className="line-clamp-3 block">{program.description}</span>
              </Fact>
            )}
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
            Cost items
          </div>
          {isLoading && <div className="text-xs text-gray-500">Loading cost items…</div>}
          {data && data.pricing.length === 0 && (
            <div className="text-xs text-gray-500">No cost items configured</div>
          )}
          {data && data.pricing.length > 0 && (
            <div className="space-y-1">
              {data.pricing.map((p) => (
                <div
                  key={p.line_item_config_id}
                  className="flex items-baseline justify-between gap-2 text-xs"
                >
                  <span className="min-w-0 break-words text-gray-800">
                    {p.name || 'Untitled item'}
                    {p.is_optional && (
                      <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-400">
                        optional
                      </span>
                    )}
                    {p.is_one_off && (
                      <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-400">
                        one-off
                      </span>
                    )}
                    <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-400">
                      {p.type}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 font-medium tabular-nums ${
                      p.type === 'discount' ? 'text-green-700' : 'text-gray-900'
                    }`}
                  >
                    {money(p.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProgramDetailPanel;

/**
 * How many times this program actually meets, and what removed the rest.
 *
 * Three rules decide it — the cron's weekdays, the school-wide closure calendar, and the
 * program's own `excluded_dates` — and until this existed an admin reading "11 sessions" in
 * a vendor's description had no way to check it. The subtraction is spelled out because the
 * total is the part people doubt.
 *
 * School closures are not listed date by date: they are shared with every other program and
 * already visible on the School Calendar page. The program's own dates ARE listed, because
 * this is the only screen that shows them at all.
 */
function SessionsFact({ program }: { program: ProgramDetailProgram }) {
  if (!program.schedule_cron || !program.start_date || !program.end_date) return null;

  let breakdown;
  try {
    breakdown = sessionBreakdown({
      type: program.type,
      schedule_cron: program.schedule_cron,
      start_date: program.start_date,
      end_date: program.end_date,
      excluded_dates: program.excluded_dates,
      // Without this the panel prints the BHCS calendar's holidays onto a Kaleido program and
      // subtracts them from its session count -- a wrong number, not just a wrong label.
      // Narrowed rather than cast: the column arrives as a plain string from `select('*')`,
      // and an unrecognized value should read as the default brand, not crash the panel.
      brand_key: isBrandKey(program.brand_key) ? program.brand_key : undefined,
    });
  } catch {
    // `getSessionWeekdays` throws on a cron it cannot read. A detail panel that white-screens
    // is worse than one missing a line.
    return null;
  }

  const own = breakdown.skipped.filter(sk => sk.source === 'program');

  return (
    <Fact label="Sessions">
      <span className="block font-medium">{breakdown.sessionCount}</span>
      <span className="block text-xs text-gray-500">{describeBreakdown(breakdown)}</span>
      {own.length > 0 && (
        <span className="block text-xs text-gray-500 mt-1">
          Program dates: {own.map(sk => sk.date).join(', ')}
        </span>
      )}
    </Fact>
  );
}
